import {
  HStack,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useContextTypeSettingsBookkeeping } from "../dashboard-layout";
import { routeSettingsBookkeeping, routeSettingsBookkeepingTaxRates, routeSettingsBookkeepingYears } from "../../routes";
import { stringMapSettingsBookkeepingTab } from "../../../strings";
import BookingAccounts from "./booking-accounts";
import TaxRates from "./tax-rates";
import BookkeepingYears from "./bookkeeping-years";
import { useEffect, useMemo } from "react";
import { aerzteTreuhandLogo, showSettings } from "../../../utils";

const settingsBookkeepingTab = ["booking-accounts", "tax-rates", "bookkeeping-years"] as const;

export type SettingsBookkeepingTab = (typeof settingsBookkeepingTab)[number];

const SettingsBookkeepingLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, selectedOrganization, selectedBookkeepingFull } =
    useContextTypeSettingsBookkeeping();

  // Check if most recent year has net tax rate (no tax rates needed)
  const hasNetTaxRate = useMemo(() => {
    if (!selectedBookkeepingFull?.bookkeepingYears?.length) return false;
    const mostRecentYear = selectedBookkeepingFull.bookkeepingYears.reduce(
      (max, by) => (by.year > max.year ? by : max)
    );
    return mostRecentYear.hasNetTaxRate;
  }, [selectedBookkeepingFull?.bookkeepingYears]);

  // Check if organization is Ärzte Treuhand
  const isAerzteTreuhand = selectedOrganization?.logo === aerzteTreuhandLogo;

  // Filter tabs based on hasNetTaxRate and organization
  const visibleTabs = useMemo(() => {
    let tabs = [...settingsBookkeepingTab];
    if (hasNetTaxRate) {
      tabs = tabs.filter((t) => t !== "tax-rates");
    }
    if (!isAerzteTreuhand) {
      tabs = tabs.filter((t) => t !== "bookkeeping-years");
    }
    return tabs;
  }, [hasNetTaxRate, isAerzteTreuhand]);

  // Determine the index of the active tab based on the current route
  const activeTabIndex = visibleTabs.findIndex((t) =>
    location.pathname.includes(t)
  );

  const handleTabsChange = (index: number) => {
    navigate(`${routeSettingsBookkeeping}/${visibleTabs[index]}`);
  };

  useEffect(() => {
    // Redirect to booking-accounts if on tax-rates route when hasNetTaxRate
    if (hasNetTaxRate && location.pathname === routeSettingsBookkeepingTaxRates) {
      navigate(`${routeSettingsBookkeeping}/${visibleTabs[0]}`);
      return;
    }
    // Redirect to booking-accounts if on bookkeeping-years route when not ÄT
    if (!isAerzteTreuhand && location.pathname === routeSettingsBookkeepingYears) {
      navigate(`${routeSettingsBookkeeping}/${visibleTabs[0]}`);
      return;
    }
    // Redirect to the first tab if the current path doesn't match any tab or is the root route
    if (
      activeTabIndex === -1 ||
      location.pathname === routeSettingsBookkeeping
    ) {
      navigate(`${routeSettingsBookkeeping}/${visibleTabs[0]}`);
    }
  }, [activeTabIndex, hasNetTaxRate, isAerzteTreuhand, location.pathname, navigate, visibleTabs]);

  if (
    user &&
    selectedOrganization &&
    !showSettings(user, selectedOrganization)
  ) {
    return <Navigate to="/" />;
  }

  return (
    <VStack
      width="full"
      height="full"
      alignItems="start"
      spacing="24px"
      py="24px"
    >
      <HStack>
        <Text fontSize={24} fontWeight="semibold" color="gray.900">
          Einstellungen
        </Text>
        {selectedBookkeepingFull ? (
          <Text fontSize={24} fontWeight="semibold" color="gray.900">
            {selectedBookkeepingFull.name}
          </Text>
        ) : (
          <Skeleton height="36px" width="120px" />
        )}
      </HStack>
      <Tabs
        width="full"
        height="full"
        variant="soft-rounded"
        colorScheme="primary"
        isLazy
        index={activeTabIndex}
        onChange={handleTabsChange}
      >
        <TabList>
          {visibleTabs.map((t: SettingsBookkeepingTab) => (
            <Tab key={t} borderRadius={6}>
              {stringMapSettingsBookkeepingTab.get(t)}{" "}
            </Tab>
          ))}
        </TabList>
        <TabPanels height="full">
          {visibleTabs.map((t: SettingsBookkeepingTab) => (
            <TabPanel key={t} height="full">
              {t === "booking-accounts" ? (
                <BookingAccounts />
              ) : t === "tax-rates" ? (
                <TaxRates />
              ) : t === "bookkeeping-years" ? (
                <BookkeepingYears />
              ) : (
                <></>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

export default SettingsBookkeepingLayout;
